/**
 * Uma seção da vitrine: título, subtítulo e a grade de cards.
 *
 * Grades do protótipo:
 *   novas  -> grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8   (imagem h-64)
 *   usadas -> grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6   (imagem h-48)
 */
import { Link } from 'react-router-dom'
import type { ProdutoResumo } from '@peniel/shared'

import { EstadoVazio } from '@/shared/ui/EstadoVazio.jsx'
import { Botao } from '@/shared/ui/Botao.jsx'
import { Icone } from '@/shared/ui/Icone.jsx'
import { CardProdutoNovo } from './CardProdutoNovo.jsx'
import { CardProdutoUsado } from './CardProdutoUsado.jsx'
import { CardProdutoEsqueleto } from './CardProdutoEsqueleto.jsx'

interface PropsSecaoProdutos {
  id: 'novas' | 'usadas'
  titulo: string
  subtitulo: string
  variante: 'nova' | 'usada'
  produtos: ProdutoResumo[] | undefined
  carregando: boolean
  erro?: unknown
  aoTentarNovamente?: () => void
  /** Total disponível na API — alimenta o "Ver todas (23)". */
  total?: number
  linkVerTodas: string
  fundo: 'cinza' | 'branco'
}

export function SecaoProdutos({
  id,
  titulo,
  subtitulo,
  variante,
  produtos,
  carregando,
  erro,
  aoTentarNovamente,
  total,
  linkVerTodas,
  fundo,
}: PropsSecaoProdutos) {
  const idTitulo = `titulo-${id}`
  const grade =
    variante === 'nova'
      ? 'grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3'
      : 'grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4'
  const quantidadeEsqueletos = variante === 'nova' ? 3 : 4

  return (
    <section
      id={id}
      aria-labelledby={idTitulo}
      className={`py-20 ${fundo === 'cinza' ? 'bg-gray-50' : 'bg-white'}`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <h2 id={idTitulo} className="mb-4 text-3xl font-bold text-gray-900">
            {titulo}
          </h2>
          <p className="mx-auto max-w-2xl text-gray-600">{subtitulo}</p>
        </div>

        {erro ? (
          <EstadoVazio
            icone="bike"
            titulo="Não conseguimos carregar as bicicletas"
            descricao="Pode ser a sua conexão. Tente novamente em instantes."
            acao={
              aoTentarNovamente && (
                <Botao variante="primaria" onClick={aoTentarNovamente}>
                  Tentar novamente
                </Botao>
              )
            }
          />
        ) : carregando ? (
          <ul className={grade}>
            {Array.from({ length: quantidadeEsqueletos }, (_, i) => (
              <CardProdutoEsqueleto key={i} variante={variante} />
            ))}
          </ul>
        ) : !produtos || produtos.length === 0 ? (
          <EstadoVazio
            icone="bike"
            titulo={
              variante === 'nova'
                ? 'Nenhuma bicicleta nova por enquanto'
                : 'Nenhuma bicicleta usada por enquanto'
            }
            descricao="Novos modelos chegam com frequência. Fale com a gente pelo WhatsApp para saber o que está a caminho."
          />
        ) : (
          <>
            <ul className={grade}>
              {produtos.map((produto, indice) =>
                variante === 'nova' ? (
                  <CardProdutoNovo
                    key={produto.id}
                    produto={produto}
                    // As 3 primeiras carregam eager: estão acima ou perto da
                    // dobra e uma delas costuma ser o LCP.
                    prioridade={indice < 3}
                  />
                ) : (
                  <CardProdutoUsado key={produto.id} produto={produto} />
                ),
              )}
            </ul>

            {total !== undefined && total > produtos.length && (
              <div className="mt-10 text-center">
                <Link
                  to={linkVerTodas}
                  className="inline-flex min-h-11 items-center gap-2 rounded-full border border-brand-600 px-6 font-semibold text-brand-700 transition hover:bg-brand-50"
                >
                  Ver todas as {variante === 'nova' ? 'novas' : 'usadas'} ({total})
                  <Icone nome="seta-direita" className="text-sm" />
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  )
}
